import React, { useEffect } from 'react';
import { cleanUrl } from 'mini-common-utils/lib/utils/cleanUrl';
import { Route, Routes, useLocation } from 'react-router-dom';
import { useNavigate } from '../hooks/useNavigate';

export interface RouteAdapterProps {
    currentPath: string;
}

/**
 * HoC that adapts react-router to work with owa-router.
 * This helps in using react-router APIs from within wrapped component tree.
 * This should go away once we move completely to react-router.
 * Caveats:
 * 1. This only works if all descendent components use react-router only for navigation.
 *    If any descendent component use owa-router for navigation, it is recommended to wrap that component in RouteAdapter as well.
 * 2. Even with this HoC in place, the page reload for descendent routes would not work. We still need to register descendent routes in owa-router config file.
 */
export const RouteAdapter: React.FC<RouteAdapterProps> = props => {
    const { currentPath, children } = props;
    const location = useLocation();
    const navigate = useNavigate();

    const cleanedCurrentPath = cleanUrl(currentPath);
    const currentPathPattern = cleanedCurrentPath + '/*';

    /**
     * Because of the nature in which owa-router operates,
     * a client side route change to the CurrentPath is not listened to by react-router.
     * So when the Children is first loaded, its location doesn't match the CurrentPath.
     * For things to render as expected, if we arrive to this Children page without CurrentPath,
     * we need to update the route to CurrentPath.
     */
    useEffect(() => {
        const { pathname: owaRouterPathname } = window.location;
        const { pathname: reactRouterPathname } = location;

        if (
            owaRouterPathname.includes(cleanedCurrentPath) &&
            !reactRouterPathname.includes(cleanedCurrentPath)
        ) {
            const locationExplicitPath = owaRouterPathname.split(cleanedCurrentPath)[1] || '';
            const newUrl = cleanUrl(`${cleanedCurrentPath}${locationExplicitPath}`);
            navigate(newUrl, { replace: true });
        }
    }, [cleanedCurrentPath, location, navigate]);

    return (
        <Routes>
            <Route path={currentPathPattern} element={<>{children}</>} />
        </Routes>
    );
};
